// The owner-set server label on a machine (#6556): `runners.server_label`, the name
// of the physical server a box runs on, so the Fleet page (#6552) can group boxes
// by server. Group key = the label when set, else the box's shared `address`
// (owner ruling G7).
//
// One rule for every door that writes it — `PUT /v1/runners/server-label` and the
// MCP `box set_server_label` action both parse with these schemas, and the control
// behind both (`apps/api` `deps/box-server-label.ts`) parses again, so no door can
// store a label another would refuse.

import { z } from 'zod';
import { BOX_NAME_MAX_CHARS, BoxNameSchema } from './fleet-task-wire';

/**
 * The label's ceiling (owner ruling G7: 64). It IS the box-name ceiling, derived
 * rather than restated, because the label is parsed by {@link BoxNameSchema}, which
 * carries that cap: one number, so the two cannot drift apart. A server label
 * renders as a group header beside box names, on one line.
 */
export const RUNNER_SERVER_LABEL_MAX_CHARS = BOX_NAME_MAX_CHARS;

/**
 * How many machines one call may label. A group header labels every machine
 * behind one address (prod 2026-10-19: largest group 6), and the bound exists so
 * one request cannot hold row locks on an unbounded set — not to ration labels.
 */
export const RUNNER_SERVER_LABEL_MAX_IDS = 500;

/**
 * A label as a caller sends it: trimmed, THEN {@link BoxNameSchema} (one line,
 * single-spaced, no controls, 1..{@link RUNNER_SERVER_LABEL_MAX_CHARS}). The stored
 * value is exactly what renders, and a blank label is refused rather than stored
 * as `""` — clearing is an explicit `null`.
 */
export const RunnerServerLabelSchema = z.string().trim().pipe(BoxNameSchema);

/**
 * The machines one call labels: 1..{@link RUNNER_SERVER_LABEL_MAX_IDS} uuids, each
 * at most once. Every id is LOWERCASED before the uniqueness check: Postgres
 * compares uuids case-insensitively and answers them lowercase, so the store's
 * id-keyed verdict (`setRunnerServerLabel`) would read an owned box named in
 * uppercase as missing, and `A…`/`a…` would pass as two machines. A repeated id is
 * refused rather than folded: the write answers per machine (one audit row each),
 * and a list that names a machine twice is a caller bug the answer would hide.
 */
export const RunnerServerLabelIdsSchema = z
  .array(z.uuid().toLowerCase())
  .min(1)
  .max(RUNNER_SERVER_LABEL_MAX_IDS)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'runner_ids must not repeat an id',
  });
